/* Blanc Project – custom theme behaviour (vanilla, no dependencies). */
(() => {
  const S = window.BlancSettings || {};
  const TZ = 'Europe/Paris';

  /* ---------- Dates (Paris time, working days) ---------- */

  // Calendar date "today" in Paris as a UTC-midnight Date, plus current Paris hh:mm.
  function parisNow() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date()).map((p) => [p.type, p.value])
    );
    return {
      date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)),
      minutes: +parts.hour * 60 + +parts.minute,
    };
  }

  const holidays = new Set((S.holidays || []).map((d) => d.trim()).filter(Boolean));
  const iso = (d) => d.toISOString().slice(0, 10);

  function isWorkingDay(d) {
    const day = d.getUTCDay();
    if (day === 0) return false;
    if (day === 6 && S.skipSaturday) return false;
    return !holidays.has(iso(d));
  }

  function addWorkingDays(start, n) {
    const d = new Date(start);
    let added = 0;
    while (added < n) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (isWorkingDay(d)) added++;
    }
    return d;
  }

  function cutoffMinutes() {
    const m = /^(\d{1,2})[:h](\d{2})$/.exec((S.cutoff || '').trim());
    return m ? +m[1] * 60 + +m[2] : null;
  }

  function deliveryWindow() {
    const now = parisNow();
    let start = now.date;
    const cutoff = cutoffMinutes();
    // An order placed on a non-working day, or after a real cut-off, is treated as placed on the next working day.
    if (!isWorkingDay(start) || (cutoff !== null && now.minutes >= cutoff)) {
      start = addWorkingDays(start, 1);
    }
    return {
      from: addWorkingDays(start, (S.handlingMin || 0) + (S.transitMin || 0)),
      to: addWorkingDays(start, (S.handlingMax || 0) + (S.transitMax || 0)),
    };
  }

  const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

  function renderDelivery(root = document) {
    const els = root.querySelectorAll('[data-blanc-delivery]');
    if (!els.length) return;
    const { from, to } = deliveryWindow();
    els.forEach((el) => {
      const a = el.querySelector('[data-blanc-delivery-from]');
      const b = el.querySelector('[data-blanc-delivery-to]');
      if (a) a.textContent = fmt.format(from);
      if (b) b.textContent = fmt.format(to);
      el.hidden = false;
    });
  }

  /* ---------- Campaign countdown ---------- */

  // "YYYY-MM-DD HH:MM" interpreted as Paris wall-clock time.
  function parseParis(str) {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec((str || '').trim());
    if (!m) return null;
    const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    const offset = (() => {
      const p = Object.fromEntries(
        new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
          .formatToParts(new Date(guess)).map((x) => [x.type, x.value])
      );
      return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - guess;
    })();
    return new Date(guess - offset);
  }

  function initCountdowns(root = document) {
    root.querySelectorAll('[data-blanc-countdown]:not([data-ready])').forEach((el) => {
      el.dataset.ready = '';
      const end = parseParis(el.dataset.end);
      if (!end) return el.remove();
      const out = el.querySelector('[data-blanc-countdown-value]');
      const tick = () => {
        const ms = end - Date.now();
        if (ms <= 0) { el.remove(); return false; }
        const d = Math.floor(ms / 864e5), h = Math.floor(ms / 36e5) % 24, m = Math.floor(ms / 6e4) % 60, s = Math.floor(ms / 1e3) % 60;
        const pad = (n) => String(n).padStart(2, '0');
        const [ud, uh, um, us] = (el.dataset.units || 'j|h|min|s').split('|');
        out.textContent = `${d > 0 ? d + ' ' + ud + ' ' : ''}${pad(h)} ${uh} ${pad(m)} ${um} ${pad(s)} ${us}`;
        el.hidden = false;
        return true;
      };
      if (tick()) {
        const id = setInterval(() => { if (!tick()) clearInterval(id); }, 1000);
      }
    });
  }

  /* ---------- Dialogs (size guide, etc.) ---------- */

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-blanc-dialog-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.blancDialogOpen);
      if (dlg && typeof dlg.showModal === 'function') {
        e.preventDefault();
        dlg.showModal();
        document.documentElement.classList.add('blanc-scroll-lock');
      }
      return;
    }
    if (e.target.closest('[data-blanc-dialog-close]')) {
      e.target.closest('dialog')?.close();
      return;
    }
    // click on backdrop
    if (e.target.matches('dialog.blanc-dialog')) e.target.close();
  });
  document.addEventListener('close', (e) => {
    if (e.target.matches?.('dialog.blanc-dialog')) document.documentElement.classList.remove('blanc-scroll-lock');
  }, true);

  /* ---------- Variant changes: swap server-rendered Blanc blocks ---------- */

  document.addEventListener('shopify:product:select', (event) => {
    event.promise?.then(({ detail } = {}) => {
      const html = detail?.html;
      if (!html?.querySelectorAll) return;
      html.querySelectorAll('[data-blanc-refresh][id]').forEach((fresh) => {
        const current = document.getElementById(fresh.id);
        if (!current) return;
        // keep an open size guide open
        const wasOpen = current.querySelector('dialog[open]');
        const next = document.importNode(fresh, true);
        current.replaceWith(next);
        if (wasOpen) next.querySelector('dialog')?.showModal();
      });
      renderDelivery();
    }).catch(() => {});
  }, true);

  /* ---------- Tabs (product tabs section) ---------- */

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-blanc-tab]');
    if (!tab) return;
    const wrap = tab.closest('[data-blanc-tabs]');
    wrap.querySelectorAll('[data-blanc-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    wrap.querySelectorAll('[data-blanc-tabpanel]').forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
  });
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest?.('[data-blanc-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-blanc-tab]')];
    const i = (tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[i].focus();
    tabs[i].click();
  });

  /* ---------- Carousel arrows ---------- */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-blanc-scroll]');
    if (!btn) return;
    const track = document.getElementById(btn.getAttribute('aria-controls'));
    if (!track) return;
    const dir = btn.dataset.blancScroll === 'next' ? 1 : -1;
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });

  /* ---------- Cart drawer recommendation ("Pour compléter") ---------- */

  async function loadCartRec(el) {
    el.dataset.loaded = '';
    const { productId, world, exclude } = el.dataset;
    if (!productId) return;
    try {
      const root = (window.Shopify?.routes?.root || '/');
      const res = await fetch(`${root}recommendations/products?product_id=${productId}&limit=8&intent=related&section_id=blanc-cart-recommendation`);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const skip = new Set((exclude || '').split(',').filter(Boolean));
      const pick = [...doc.querySelectorAll('[data-blanc-rec-item]')].find(
        (i) => i.dataset.world === world && !skip.has(i.dataset.productId)
      );
      if (!pick) return;
      el.querySelector('[data-blanc-rec-slot]').replaceChildren(document.importNode(pick, true));
      el.hidden = false;
    } catch (_) { /* recommendation is optional */ }
  }

  function scan(root = document) {
    root.querySelectorAll?.('[data-blanc-cart-rec]:not([data-loaded])').forEach(loadCartRec);
    renderDelivery(root);
    initCountdowns(root);
  }

  // The cart drawer and other sections are re-rendered by Dwell; pick up new Blanc nodes.
  new MutationObserver((records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType === 1 && (n.matches?.('[data-blanc-cart-rec],[data-blanc-delivery],[data-blanc-countdown]') || n.querySelector?.('[data-blanc-cart-rec],[data-blanc-delivery],[data-blanc-countdown]'))) {
          scan(n.parentNode || document);
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });


  /* ---------- Recently viewed (per-viewer, browser storage only) ---------- */

  const RV_KEY = 'blanc:recent';
  function readRecent() {
    try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (_) { return []; }
  }
  function writeRecent(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 16))); } catch (_) { /* storage unavailable */ }
  }

  async function initRecent() {
    const wrap = document.querySelector('[data-blanc-recent]');
    const current = wrap?.dataset.current || document.querySelector('[data-blanc-product-handle]')?.dataset.blancProductHandle;
    const list = readRecent().filter((h) => h !== current);
    if (current) writeRecent([current, ...list]);
    if (!wrap) return;
    const handles = list.slice(0, +wrap.dataset.limit || 8);
    if (!handles.length) return;
    const root = (window.Shopify?.routes?.root || '/');
    const cards = await Promise.all(handles.map((h) =>
      fetch(`${root}products/${encodeURIComponent(h)}?view=blanc-card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')
    ));
    const html = cards.filter((c) => c.trim()).join('');
    if (!html) return;
    wrap.querySelector('[data-blanc-recent-track]').innerHTML = html;
    wrap.hidden = false;
  }

  const boot = () => { scan(); initRecent(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
